import { Injectable, inject } from '@angular/core';

import { FixturePage, FixtureType, OfferCategory } from '@cds/betting-offer';
import { FixtureRequest, FixtureState, OfferMapping, SortByCriteria } from '@cds/query-objects';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { CalendarService } from '@frontend/sports/calendar/feature';
import { GridConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { CountItem, sportModelMethods } from '@frontend/sports/common/core/data-access/sport-model';
import { GridSorting } from '@frontend/sports/grid/core/feature/model';
import { CompetitionRoute } from '@frontend/sports/navigation/core/feature/models';
import { MarketOfferUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { StatisticsConfigService } from '@frontend/sports/statistics/core/feature';
import { Observable, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class EventListService {
    protected bettingContent = inject(BettingOfferApi);
    protected calendarService = inject(CalendarService);
    protected gridConfig = inject(GridConfig);
    protected bettingCache = inject(SportsCacheService);
    protected statisticsConfigService? = inject(StatisticsConfigService, { optional: true });

    getSportList(start?: string, end?: string): Observable<CountItem[]> {
        const cached = !start && !end;

        const source = cached
            ? this.bettingCache.getSportList()
            : this.bettingContent
                  .getSportList({
                      from: start,
                      to: end,
                  })
                  .pipe(map((sports) => sportModelMethods.fromTag(sports)));

        return source;
    }

    getFixtureList(
        route: CompetitionRoute,
        sorting?: GridSorting,
        skip?: number,
        take?: number,
        interval: { from?: string; to?: string } = {},
    ): Observable<FixturePage | undefined> {
        const skipAmount = skip || 0;
        const takeAmount = take || this.gridConfig.initialSize;

        const fixtureRequest = this.createFixtureRequest(route);
        const dateInterval = this.calendarService.getTimeInterval(route.context) || interval;
        const hasSpecifiedDateInterval = dateInterval.from || dateInterval.to;
        const hasSpecifiedRegion = route.region || route.regionName;

        if (!fixtureRequest.dynamicOfferCategories && (hasSpecifiedDateInterval || route.tournament)) {
            const categories = [OfferCategory.Gridable, OfferCategory.NonGridable, OfferCategory.Other, OfferCategory.Outrights];
            const isEnhancedAccasHidden = !!route.sport && this.calendarService.isEnhancedAccasHidden(route.sport);

            if (!hasSpecifiedDateInterval || hasSpecifiedRegion || !isEnhancedAccasHidden) {
                categories.push(OfferCategory.Specials);
            }

            fixtureRequest.fixtureCategories = categories.join(',');

            fixtureRequest.offerCategories =
                route.marketOffer === MarketOfferUrlParam.Specials || route.marketOffer === MarketOfferUrlParam.Outrights
                    ? [OfferCategory.Outrights, OfferCategory.Specials].join(',')
                    : route.marketOffer === MarketOfferUrlParam.NonGridable
                      ? OfferCategory.NonGridable
                      : OfferCategory.Gridable;
        }

        fixtureRequest.skip = skipAmount;
        fixtureRequest.take = takeAmount;
        fixtureRequest.sortBy = sorting === GridSorting.Time ? SortByCriteria.StartDate : SortByCriteria.Tags;
        fixtureRequest.from = dateInterval.from;
        fixtureRequest.to = dateInterval.to;

        return this.bettingContent.getFixtureList(fixtureRequest);
    }

    createFixtureRequest(route: CompetitionRoute): FixtureRequest {
        let offerCategories: string | undefined;
        let offerTemplates: string | undefined;
        let fixtureCategories: string | undefined;
        let isPriceBoost = false;
        const hasSubcategories = route.outrightCategory || route.specialCategory;

        if (route.marketTemplate) {
            offerTemplates = route.marketTemplate.toString();
        } else {
            switch (route.marketOffer) {
                case MarketOfferUrlParam.PriceBoost:
                    isPriceBoost = true;

                    break;

                case MarketOfferUrlParam.Outrights:
                    offerCategories = OfferCategory.Outrights;
                    fixtureCategories = hasSubcategories ? undefined : OfferCategory.Outrights;

                    break;

                case MarketOfferUrlParam.Specials:
                    offerCategories = OfferCategory.Specials;
                    fixtureCategories = hasSubcategories ? undefined : OfferCategory.Specials;

                    break;

                default:
                    offerCategories = route.marketOffer === MarketOfferUrlParam.NonGridable ? OfferCategory.NonGridable : OfferCategory.Gridable;

                    fixtureCategories = [OfferCategory.Gridable, OfferCategory.NonGridable, OfferCategory.Other].join(',');
            }
        }

        return {
            fixtureTypes: FixtureType.Standard,
            state: FixtureState.Latest,
            offerMapping: OfferMapping.Filtered,
            offerCategories: route.dynamicOfferCategory ? undefined : offerCategories,
            dynamicOfferCategories: route.dynamicOfferCategory,
            outrightGroups: route.outrightCategory,
            specialGroups: route.specialCategory,
            fixtureCategories: route.dynamicOfferCategory ? undefined : fixtureCategories,
            sportIds: route.sport?.toString(),
            regionIds: route.sport === SportConstant.Tennis ? undefined : route.region?.toString(),
            tournamentIds: route.sport === SportConstant.Tennis ? route.region?.toString() : undefined,
            competitionIds: route.isVirtual ? undefined : route.league?.toString(),
            conferenceIds: route.isVirtual ? undefined : route.conference?.toString(),
            virtualCompetitionIds: route.isVirtual ? route.league?.toString() : undefined,
            virtualCompetitionGroupIds: (route.isVirtual && route.virtualCompetitionGroup?.toString()) || undefined,
            gameTemplateIds: offerTemplates,
            isPriceBoost,
            statisticsModes: this.statisticsConfigService?.getStatisticsModesBySportId(route.sport),
        };
    }
}
